import { Fragment, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";

import { CustomPagination } from "@components/CustomPagination";

import { formatDateToString } from "@utils/dateValidation";

import { DEFAULT_PAGINATION, QP } from "@constants/index";

import { useGetPatientsBySearchOnly } from "@hooks/queries/patient/useGetPatientsBySearchOnly";
import { useSyncTableQueryParams } from "@hooks/useSyncTableQueryParams";

import { SelectedRow } from "@customTypes/index";
import { Patient } from "@customTypes/patient";

import { StyledTable } from "@styles/components/Table.styled";

import { ViewProfileModal } from "../ViewProfileModal";


const columnHelper = createColumnHelper<Partial<Patient>>();

interface PatientsTableProps {
  searchFilter: string;
  setIsRefreshing: (arg: boolean) => void;
  setTotalNumOfPatients: (arg: number) => void;
}

export const PatientsTable = ({
  searchFilter,
  setIsRefreshing,
  setTotalNumOfPatients,
}: PatientsTableProps) => {
  const { t, i18n } = useTranslation();

  const { debounced, tablePagination, setTablePagination } =
    useSyncTableQueryParams({ searchFilter });

  const [selectedRow, setSelectedRow] = useState<SelectedRow<Partial<Patient>>>(
    {}
  );
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [tableData, setTableData] = useState<Partial<Patient>[] | []>([]);
  const [opened, { open, close }] = useDisclosure(false);

  const {
    data,
    isError,
    isSuccess,
    isLoading,
    fetchStatus,
    isRefetching,
    refetch, //* Temporary
  } = useGetPatientsBySearchOnly({
    pagination: {
      page: tablePagination.page,
      pageSize: tablePagination.pageSize,
    },
    search: debounced.toLocaleLowerCase(),
    fields: ["patient_id", "first_name", "surname", "dob", "created_at"],
    isEnabled: false, //* Temporary
  });

  const patientColumns = useMemo(
    () => [
      columnHelper.accessor("patientId", {
        header: t("patient-id"),
        cell: info => (
          <Text inherit span ff={"var(--font-secondary)"}>
            {info.getValue()}
          </Text>
        ),
      }),
      columnHelper.accessor("firstName", {
        header: t("first-name"),
      }),
      columnHelper.accessor("surname", {
        header: t("surname"),
      }),
      columnHelper.accessor("dob", {
        header: t("dob"),
      }),
      columnHelper.accessor("createdAt", {
        header: t("created-at"),
        cell: info => formatDateToString(info.cell.getValue() as Date),
      }),
    ],
    [tableData, i18n.language]
  );

  const table = useReactTable({
    data: tableData,
    columns: patientColumns,
    // state: {
    //   globalFilter: searchFilter,
    // },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    // onGlobalFilterChange: setSearchFilter,
    // globalFilterFn: (
    //   row: Row<Partial<Patient>>,
    //   columnId: string,
    //   value: string
    // ) => {
    //   if (
    //     columnId === "firstName" ||
    //     columnId === "surname" ||
    //     columnId === "patientId"
    //   ) {
    //     return (row.getValue(columnId) as string)
    //       .toLocaleLowerCase()
    //       .includes(value);
    //   }
    //   return false;
    // },
  });

  useEffect(() => {
    if (data) {
      setTableData(data.newData);
      setTablePagination(data.pagination);
      setTotalNumOfPatients(data.pagination.total);
    } else {
      setTableData([]);
      setTotalNumOfPatients(0);
    }
  }, [data]);

  // Previous logic
  // useEffect(() => {
  //   // Initial load of data
  //   if (tablePagination.page && !debounced) {
  //     refetch();
  //   }
  //   // Filters are updated and page is changed
  //   // Can be combined with condition above, but for readability it will be separated
  //   else if (debounced && tablePagination.page !== previousPaginationPage) {
  //     refetch();
  //   }
  //   // Filter is updated, reset pagination
  //   else if (debounced) {
  //     // console.log("in debounced");
  //     setTablePagination(prev => ({ ...prev, page: 1 }));
  //     refetch();
  //   }
  // }, [debounced, tablePagination.page]);

  useEffect(() => {
    if (setIsRefreshing) setIsRefreshing(isRefetching);
  }, [isRefetching]);

  //* Temporary ------------------------------------
  useEffect(() => {
    if (debounced || tablePagination.page) refetch();
    else {
      setTableData([]);
      setTablePagination({ ...DEFAULT_PAGINATION });
    }
  }, [debounced, tablePagination.page]);
  //* End Temporary --------------------------------

  return (
    <>
      <StyledTable $hasPointerCursorOnHover>
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        {isSuccess && !data?.newData?.length && debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={patientColumns.length}>
                <p>
                  {t("empty-results-for", {
                    searchFilter: `"${debounced}"`,
                  })}
                </p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {/* {isSuccess && !data?.newData?.length && !debounced ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={patientColumns.length}>
                <p>{t("empty-patients")}</p>
              </td>
            </tr>
          </tbody>
        ) : null} */}

        {isLoading && fetchStatus === "fetching" && !tableData.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={patientColumns.length}>
                <p>{t("loading")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {isError && !data?.newData?.length ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={patientColumns.length}>
                <p>{t("encountered-error")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}

        {/* //* Temporary -----------------------------------------*/}
        {!data?.newData?.length &&
        !debounced &&
        !isLoading &&
        fetchStatus !== "fetching" ? (
          <tbody>
            <tr className="helper-row">
              <td colSpan={patientColumns.length}>
                <p>{t("search-for-your-patient")}</p>
              </td>
            </tr>
          </tbody>
        ) : null}
        {/* //* End Temporary -----------------------------------------*/}

        <tbody>
          {table.getRowModel().rows.map(row => (
            <Fragment key={row.id}>
              <ViewProfileModal
                opened={opened}
                close={close}
                row={selectedRow}
                rowId={row?.id}
                reason={reasons[row.id]}
                setReason={value =>
                  setReasons(prev => ({ ...prev, [row.id]: value }))
                }
              />
              <tr
                key={row.id}
                onClick={() => {
                  setSelectedRow(row);
                  open();
                }}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            </Fragment>
          ))}
        </tbody>
      </StyledTable>

      {(isLoading && fetchStatus === "fetching") || !data ? null : (
        <CustomPagination
          table={table}
          tablePagination={tablePagination}
          otherParams={{
            ...(debounced && { [QP.SEARCH]: debounced }),
          }}
        />
      )}
    </>
  );
};
